import { useState, useEffect } from 'react';

import {AppShell, Burger, Box, NavLink, ActionIcon, MantineProvider, createTheme, Group, Text, Select} from '@mantine/core';

import '@mantine/core/styles.css';

import {Route, Switch, Link, useLocation} from "wouter";

import {
  IconSun, IconMoon,
  IconBulb, IconBrain, IconCode, IconTag,
  IconFileText, IconShieldLock, IconInfoCircle, IconArticle,
  IconGitBranch, IconBuildingFactory, IconLogin2, IconLogout2,
  IconExternalLink, IconLink,
} from "@tabler/icons-react";

import {useTranslation} from "react-i18next";

import ImplementationGraphic from './ImplementationGraphic.jsx';
import ConceptGraphic from './ConceptGraphic.jsx';
import Configuration from './Configuration.jsx';
import Status from './Status2.jsx';
import Pricing from './Pricing.jsx';
import RepositoryConnected from './RepositoryConnected.jsx';
import SampleIntegration from './SampleIntegration.jsx';
import Verification from './Verification.jsx';
import PrivacyPolicy from './PrivacyPolicy.jsx';
import TermsOfService from './TermsOfService.jsx';
import Imprint from './Imprint.jsx';
import Enterprise from './Enterprise.jsx';
import DockerLicense from './DockerLicense.jsx';
import Motivations from './Motivations.jsx';

import {TimestampGitLogo} from "./Logo.jsx";
import LanguageFlag from "./LanguageFlag.jsx";

import useUser from "./userProvider";
import useConfig from "./configProvider";
import useSubscription from "./subscriptionProvider";

import RepositoryTable from "./RepositoryTable.jsx";
import RepoSelector from "./RepoSelector.jsx";
import ZkTimestamp from "./ZkTimestamp.jsx";

import { STORAGE_KEY, supportedLanguages } from "./i18n.js";

import "./global.css";

const  App = () => {
  const [opened, setOpened] = useState(false);
  const [asideOpened, setAsideOpened] = useState(false);

  const [colorScheme, setColorScheme] = useState('dark');

  const {t, i18n} = useTranslation();

  const config = useConfig();

  const [location, setLocation] = useLocation();

  const {userInfo} = useUser();
  const {data: subscription} = useSubscription(userInfo?.sub);
  const isZkAdmin = subscription != null && ["ZK", "Admin"].includes(subscription);
  const [language, setLanguage] = useState((i18n.resolvedLanguage || i18n.language || "en").slice(0, 2));

  // Blog language codes differ from the app's i18n codes (e.g. ja -> jp)
  const blogLangCode = language === 'ja' ? 'jp' : language;
  const blogHref = language === 'en' ? '/blog/index.html' : `/blog/${blogLangCode}/index.html`;

  const toggle = () => {
    if (!opened) setAsideOpened(false);
    setOpened((o) => !o);
  };

  const toggleColorScheme = () => {
      setColorScheme(colorScheme === 'dark' ? 'light' : 'dark');
      console.log("Color scheme toggled to:", colorScheme);
       }

  const toggleAside = () => {
    if (!asideOpened) setOpened(false);
    setAsideOpened((o) => !o);
  };

  useEffect(() => {
      switch (config?.data?.mode) {
          case "setup":
              setLocation("/enterprise/setup");
              break;

          case "setup2":
              setLocation("/enterprise/setup2");
              break;

          default:
              break;
        }

    }, [config]);

  useEffect(() => {
      const currentLanguage = (i18n.resolvedLanguage || i18n.language || "en").slice(0, 2);
      if (supportedLanguages.includes(currentLanguage) && currentLanguage !== language) {
          setLanguage(currentLanguage);
      }
  }, [i18n.language, i18n.resolvedLanguage, language]);

  const handleLanguageChange = async (value) => {
      if (!supportedLanguages.includes(value)) {
          return;
      }

      setLanguage(value);
      localStorage.setItem(STORAGE_KEY, value);
      await i18n.changeLanguage(value);
  };

  const theme = createTheme();

  return <>
            <MantineProvider theme={theme} forceColorScheme={colorScheme} >
              <AppShell header={{ height: 60 }}
                        navbar={{
                                    width: 300,
                                    breakpoint: 'sm',
                                    collapsed: { mobile: !opened, desktop: !opened },
                                }}
                        aside={{
                                    width: 300,
                                    breakpoint: 'sm',
                                    collapsed: { mobile: !asideOpened, desktop: !asideOpened },
                                }}>
                  <AppShell.Header>
                    <Group h="100%" px="md" wrap="nowrap" gap="sm" justify="space-between">
                      <Group gap="sm" wrap="nowrap">
                        <Burger opened={opened} onClick={toggle} />
                        <Box maw={160}>
                          <TimestampGitLogo noDescription style={{display: 'block', width: '100%', height: 60}} />
                        </Box>
                      </Group>
                      <Group gap="sm" wrap="nowrap">
                        <Select
                          size="xs"
                          w={150}
                          allowDeselect={false}
                          value={language}
                          onChange={handleLanguageChange}
                          aria-label={t("Language")}
                          leftSection={<LanguageFlag code={language} size={14} />}
                          renderOption={({ option }) => (
                            <Group gap={8} wrap="nowrap">
                              <LanguageFlag code={option.value} size={14} />
                              <span>{option.label}</span>
                            </Group>
                          )}
                          data={[
                            { value: "en", label: "English" },
                            { value: "de", label: "Deutsch" },
                            { value: "es", label: "Español" },
                            { value: "pt", label: "Português" },
                            { value: "it", label: "Italiano" },
                            { value: "fr", label: "Français" },
                            { value: "ja", label: "日本語" },
                            { value: "zh", label: "中文" },
                            { value: "ru", label: "Русский" },
                          ]}
                        />
                        <ActionIcon variant="default" onClick={toggleColorScheme} size="lg" aria-label={t("Toggle color scheme")}>
                          <IconSun />
                        </ActionIcon>
                        {userInfo?.name && <Text>{userInfo?.name}</Text>}
                        <Burger opened={asideOpened} onClick={toggleAside} aria-label={t("Toggle info panel")} />
                      </Group>
                    </Group>
                  </AppShell.Header>
                  <AppShell.Navbar opened={opened}>
                      {t("Menu")}
                      {userInfo != null &&
                        <NavLink component={Link} href={"/repos"} label={t("Your Repositories")} leftSection={<IconGitBranch size="1.2rem" stroke={1.5} />} onClick={() => setOpened(false)} />
                      }
                      {userInfo != null && isZkAdmin &&
                        <NavLink component={Link} href={"/zk-timestamp"} label={t("ZK Timestamp URL")} leftSection={<IconLink size="1.2rem" stroke={1.5} />} onClick={() => setOpened(false)} />
                      }
                      {/* Workspace configuration disabled for now
                      {userInfo != null &&
                        <NavLink component={Link} href={`/config/${userInfo.sub}`} label={t("Configuration")} />
                      }
                      */}
                      {userInfo != null && config?.data?.adminUser && userInfo.sub === config.data.adminUser &&
                        <NavLink component={Link} href={"/enterprise/setup"} label={t("Enterprise Setup")} leftSection={<IconBuildingFactory size="1.2rem" stroke={1.5} />} onClick={() => setOpened(false)} />
                      }

                      {userInfo == null &&
                        <NavLink component="a" href={"/api/login?cont=/repos"} label={t("Login")} leftSection={<IconLogin2 size="1.2rem" stroke={1.5} />} onClick={() => setOpened(false)} />
                        }
                      {userInfo != null &&
                        <NavLink component="a" href={"/api/logout"} label={t("Logout")} leftSection={<IconLogout2 size="1.2rem" stroke={1.5} />} onClick={() => setOpened(false)} />
                        }

                  </AppShell.Navbar>
                  <AppShell.Aside opened={asideOpened}>
                      {t("Information")}
                      <NavLink component={Link} href={"/problem"} label={t("Problem")} leftSection={<IconBulb size="1.2rem" stroke={1.5} />} onClick={() => setAsideOpened(false)} />
                      <NavLink component={Link} href={"/concept"} label={t("Concept")} leftSection={<IconBrain size="1.2rem" stroke={1.5} />} onClick={() => setAsideOpened(false)} />
                      <NavLink component={Link} href={"/implementation"} label={t("Implementation")} leftSection={<IconCode size="1.2rem" stroke={1.5} />} onClick={() => setAsideOpened(false)} />
                      <NavLink component="a" href={blogHref} label={t("Blog")} leftSection={<IconArticle size="1.2rem" stroke={1.5} />} onClick={() => setAsideOpened(false)} />
                      <NavLink component={Link} href={"/pricing"} label={t("Pricing")} leftSection={<IconTag size="1.2rem" stroke={1.5} />} onClick={() => setAsideOpened(false)} />
                      <NavLink component={Link} href={"/terms"} label={t("Terms of Service")} leftSection={<IconFileText size="1.2rem" stroke={1.5} />} onClick={() => setAsideOpened(false)} />
                      <NavLink component={Link} href={"/privacy"} label={t("Privacy Policy")} leftSection={<IconShieldLock size="1.2rem" stroke={1.5} />} onClick={() => setAsideOpened(false)} />
                      <NavLink component={Link} href={"/imprint"} label={t("Imprint")} leftSection={<IconInfoCircle size="1.2rem" stroke={1.5} />} onClick={() => setAsideOpened(false)} />
                      <NavLink component="a" href="https://py2native.dev" target="_blank" rel="noopener noreferrer" label={t("Py2Native")} leftSection={<IconExternalLink size="1.2rem" stroke={1.5} />} onClick={() => setAsideOpened(false)} />
                  </AppShell.Aside>
                  <AppShell.Main>

                      <Box w={"100%"} h={"80%"} p={16}>
                        <Switch>
                            <Route path="/" component={Motivations} />
                            <Route path="/problem" component={Motivations} />
                            <Route path="/concept" component={ConceptGraphic} />
                            <Route path="/pricing" component={Pricing} />
                            <Route path="/docker-license" component={DockerLicense} />
                            <Route path="/implementation" component={ImplementationGraphic} />
                            <Route path="/repos" component={RepoSelector} />
                            <Route path="/status/:user/:repo" component={Status} />
                            <Route path={"/connected/:user/:repo"} component={RepositoryConnected} />
                            <Route path={"/config/:user"} component={Configuration} />
                            <Route path={"/sample"} component={SampleIntegration} />
                            <Route path={"/zk-timestamp"} component={ZkTimestamp} />
                            <Route path={"/verification/:user/:repo/:date"} component={Verification} />
                            <Route path={"/terms"} component={TermsOfService} />
                            <Route path={"/privacy"} component={PrivacyPolicy} />
                            <Route path={"/imprint"} component={Imprint} />
                            <Route path={"/enterprise/:phase"} component={Enterprise} />
                        </Switch>

                      </Box>

                  </AppShell.Main>
              </AppShell>
            </MantineProvider>
         </>;
  }

export default App;